import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Typography } from '@gapartners/ui';
import { getCaseNotesSaveEligibility } from '@/utils/caseNotes';

interface CaseNotesSaveStatusProps {
  lastSaved: string | null | undefined;
  current: string;
  /** NotesCard: eligible changes will auto-save after a delay. */
  autoSave?: boolean;
  onDiscard?: () => void;
}

export function CaseNotesSaveStatus({
  lastSaved,
  current,
  autoSave = false,
  onDiscard,
}: CaseNotesSaveStatusProps) {
  const { t } = useTranslation();
  const eligibility = useMemo(
    () => getCaseNotesSaveEligibility(lastSaved, current),
    [lastSaved, current]
  );

  if (eligibility === 'unchanged') return null;

  if (eligibility === 'appendable') {
    return (
      <Typography variant="caption" color="success.main">
        {autoSave
          ? t('components.contactCenter.caseNotesPendingAutoSave')
          : t('components.contactCenter.caseNotesReadyToSave')}
      </Typography>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        flexWrap: 'wrap',
        justifyContent: 'flex-end',
      }}
    >
      <Typography variant="caption" color="warning.main">
        {t('components.contactCenter.caseNotesAppendOnly')}
      </Typography>
      {onDiscard && (
        <Button
          variant="text"
          size="small"
          onClick={onDiscard}
          sx={{
            minWidth: 0,
            py: 0,
            px: 0.5,
            fontSize: '0.75rem',
            lineHeight: 1.2,
            textTransform: 'none',
          }}
        >
          {t('components.contactCenter.caseNotesDiscardChanges')}
        </Button>
      )}
    </Box>
  );
}
